import React, { useState, useEffect } from "react";
import { getNonce, getTables, getFormSettings } from './../Helpers';
import TabSettingsIcon from '@mui/icons-material/Settings';
import TabDashboardIcon from '@mui/icons-material/Dashboard';
import TabSmartToyRoundedIcon from '@mui/icons-material/SmartToyRounded';
import TelegramIcon from '@mui/icons-material/Telegram';
import TabSaveRoundedIcon from '@mui/icons-material/SaveRounded';
import NoticeModal from '../components/NoticeModal/NoticeModal';

import GeneralTab from './tabs/GeneralTab';
import ThemeTab from './tabs/ThemeTab';
import CaptchaTab from './tabs/CaptchaTab';
import TelegramTab from './tabs/TelegramTab';
import "../styles/_setting.scss";

// Hook to handle local storage for a specific key
const useLocalStorage = (key, defaultValue) => {
  const [state, setState] = useState(() => {
    const storedState = localStorage.getItem(key);
    try {
      return storedState ? JSON.parse(storedState) : defaultValue;
    } catch (error) {
      console.error(`Error parsing JSON for key ${key}:`, error);
      return defaultValue;
    }
  });

  useEffect(() => {
    localStorage.setItem(key, JSON.stringify(state));
  }, [key, state]);

  return [state, setState];
};

const Settings = () => {
  // Basic UI state with localStorage persistence
  const [activeTab, setActiveTab] = useState(() => {
    // Load active tab from localStorage or default to 'settings'
    return localStorage.getItem('simpleform_settings_active_tab') || 'settings';
  });
  const [tables, setTables] = useState(getTables());
  const [formSettings, setSettings] = useState(getFormSettings());

  // Required settings variables - keeping only what's needed
  const [selectedTheme, setSelectedTheme] = useLocalStorage("selectedTheme", "theme1");
  const [storeleds, setStoreleds] = useLocalStorage('storeleds', formSettings.storeleds || true);
  const [metadata, setMetadata] = useLocalStorage('metadata', formSettings.metadata || true);
  const [leadsinSlack, setLeadsinSlack] = useLocalStorage('leadsinSlack', formSettings.leadsinSlack || false);
  const [recipiantslack, setRecipiantslack] = useLocalStorage('recipiantslack', formSettings.recipiantslack || '');
  const [leadsinTelegram, setLeadsinTelegram] = useLocalStorage('leadsinTelegram', formSettings.leadsinTelegram || false);
  const [telegramBotToken, setTelegramBotToken] = useLocalStorage('telegramBotToken', formSettings.telegramBotToken || '');
  const [telegramChatId, setTelegramChatId] = useLocalStorage('telegramChatId', formSettings.telegramChatId || '');
  const [cloudflareCaptchaEnable, setCloudflareCaptchaEnable] = useLocalStorage('cloudflareCaptchaEnable', formSettings.cloudflareCaptchaEnable || false);
  const [siteKey, setSiteKey] = useLocalStorage('siteKey', formSettings.siteKey || "");
  const [secretKey, setSecretKey] = useLocalStorage('secretKey', formSettings.secretKey || "");

  // Modal state
  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    type: 'confirmation',
    title: '',
    message: '',
    onConfirm: () => { },
    confirmText: 'Confirm',
    declineText: 'Cancel',
    mode: 'success'
  });

  const closeModal = () => {
    setModalConfig(prev => ({ ...prev, isOpen: false }));
  };


  const handleTabClick = (tab) => {
    setActiveTab(tab);
    // Save active tab to localStorage
    localStorage.setItem('simpleform_settings_active_tab', tab);
  };

  // Load tables
  useEffect(() => {
    wp.ajax.send('simpleform_get_tables', {
      data: {
        nonce: getNonce(),
      },
      success(response) {
        setTables(response.tables);
      },
      error(error) {
        console.error(error);
      },
    });
  }, []);

  // Load settings
  useEffect(() => {
    wp.ajax.send('simpleform_get_settings', {
      data: {
        nonce: getNonce(),
      },
      success(response) {
        setSettings(response.settings);
      },
      error(error) {
        console.error(error);
      },
    });
  }, []);



  // Save settings
  const handleSubmit = (e) => {
    e.preventDefault();

    // Collect all necessary settings
    const settings = {
      selectedTheme,
      storeleds,
      metadata,
      leadsinSlack,
      recipiantslack,
      leadsinTelegram,
      telegramBotToken,
      telegramChatId,
      cloudflareCaptchaEnable,
      siteKey,
      secretKey
    };

    setModalConfig({
      isOpen: true,
      type: 'confirmation',
      title: 'Save',
      mode: 'success',
      message: 'Are you done!',
      onConfirm: () => {
        wp.ajax.send('simpleform_save_settings', {
          data: {
            nonce: getNonce(),
            settings: settings,
          },
          success(response) {
            wp.ajax.send('simpleform_get_settings', {
              data: {
                nonce: getNonce(),
              },
              success(response) {
                setSettings(response.settings);
              },
              error(error) {
                console.error(error);
              },
            });

            setModalConfig({
              isOpen: true,
              type: 'toast',
              title: 'Success',
              message: 'Settings have been saved.',
              position: 'top-right',
              mode: 'success',
            });
            setTimeout(closeModal, 3000);
          },
          error(error) {
            setModalConfig({
              isOpen: true,
              type: 'toast',
              title: 'Error!',
              message: 'Failed to store settings.',
              position: 'top-right',
              mode: 'error',
            });
            setTimeout(closeModal, 3000);
          },
        });
        closeModal();
      },
      confirmText: 'Yes, Save!',
      declineText: 'Cancel'
    });
  };

  return (
    <div className="acb_bottom" id="acb_bottom">
      <div className="acb_left">
        {/* Tab Headers */}
        <div className="tab-headers">
          <div
            className={`tab-header ${activeTab === "settings" ? "active" : ""}`}
            onClick={() => handleTabClick("settings")}
          >
            <TabSettingsIcon className="TabIcon" />
            General
          </div>

          <div className="horizontal-line"></div>

          <div
            className={`tab-header ${activeTab === "form-theme" ? "active" : ""}`}
            onClick={() => handleTabClick("form-theme")}
          >
            <TabDashboardIcon className="TabIcon" />
            Theme
          </div>

          <div className="horizontal-line"></div>

          <div
            className={`tab-header ${activeTab === "captcha" ? "active" : ""}`}
            onClick={() => handleTabClick("captcha")}
          >
            <TabSmartToyRoundedIcon className="TabIcon" />
            Captcha
          </div>

          <div className="horizontal-line"></div>

          <div
            className={`tab-header ${activeTab === "telegram" ? "active" : ""}`}
            onClick={() => handleTabClick("telegram")}
          >
            <TelegramIcon className="TabIcon" />
            Telegram
          </div>

          <div className="horizontal-line"></div>
          <div className="horizontal-line"></div>

          <div
            className={`tab-header setings-submit-btn`}
            onClick={handleSubmit}
          >
            <TabSaveRoundedIcon className="TabIcon" />
            SAVE
          </div>
        </div>

        {/* Tab Content */}
        <div className={`tab-content settings-tab ${activeTab === "settings" ? "active" : ""}`}>
          {activeTab === "settings" && (
            <GeneralTab
              storeleds={storeleds}
              setStoreleds={setStoreleds}
              metadata={metadata}
              setMetadata={setMetadata}
              leadsinSlack={leadsinSlack}
              setLeadsinSlack={setLeadsinSlack}
              recipiantslack={recipiantslack}
              setRecipiantslack={setRecipiantslack}
            />
          )}
        </div>

        <div className={`tab-content form-tab ${activeTab === "form-theme" ? "active" : ""}`}>
          {activeTab === "form-theme" && (
            <ThemeTab
              selectedTheme={selectedTheme}
              setSelectedTheme={setSelectedTheme}
            />
          )}
        </div>

        <div className={`tab-content captcha-tab ${activeTab === "captcha" ? "active" : ""}`}>
          {activeTab === "captcha" && (
            <CaptchaTab
              cloudflareCaptchaEnable={cloudflareCaptchaEnable}
              setCloudflareCaptchaEnable={setCloudflareCaptchaEnable}
              siteKey={siteKey}
              setSiteKey={setSiteKey}
              secretKey={secretKey}
              setSecretKey={setSecretKey}
              setModalConfig={setModalConfig}
              closeModal={closeModal}
            />
          )}
        </div>

        <div className={`tab-content telegram-tab ${activeTab === "telegram" ? "active" : ""}`}>
          {activeTab === "telegram" && (
            <TelegramTab
              leadsinTelegram={leadsinTelegram}
              setLeadsinTelegram={setLeadsinTelegram}
              telegramBotToken={telegramBotToken}
              setTelegramBotToken={setTelegramBotToken}
              telegramChatId={telegramChatId}
              setTelegramChatId={setTelegramChatId}
              setModalConfig={setModalConfig}
              closeModal={closeModal}
            />
          )}
        </div>
      </div>

      <NoticeModal
        isOpen={modalConfig.isOpen}
        onClose={closeModal}
        onConfirm={modalConfig.onConfirm}
        onDecline={closeModal}
        type={modalConfig.type}
        mode={modalConfig.mode}
        title={modalConfig.title}
        message={modalConfig.message}
        confirmText={modalConfig.confirmText}
        declineText={modalConfig.declineText}
        position={modalConfig.position || 'center'}
        autoClose={modalConfig.type === 'toast'}
        autoCloseTime={3000}
      />
    </div>
  );
};

export default Settings;